import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MatLegacyTabChangeEvent as MatTabChangeEvent,
  MatLegacyTabGroup as MatTabGroup,
} from '@angular/material/legacy-tabs';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { concat, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { router } from '@uni/router/router';
import { AuthService } from 'app/core/auth';
import { BusinessTypeService } from 'app/core/business-type';
import { ClientShippingAddressService } from 'app/core/client/services/client-shipping-address.service';
import { ClientTypeService } from 'app/core/client/services/client-type.service';
import { ClientUsersService } from 'app/core/client/services/client-users.service';
import { ClientService } from 'app/core/client/services/client.service';
import { BusinessAccountContact } from 'app/core/client/store/client.model';
import { BusinessAccountContactsService } from 'app/core/client-user/services/business-account-contacts.service';
import { ClientContactUtilsService } from 'app/core/client-user/services/client-contact-utils.service';
import { ProductTagService } from 'app/core/product-tags/services/product-tag.service';
import { StaffService } from 'app/core/staff/services/staff.service';
import { Staff, UserRoleConstants } from 'app/core/staff/store/staff.model';
import { EFeatureFlag } from 'app/feature-toggle-list';
import { BusinessAccountContactInterface } from 'app/modules/clients/interfaces/business-contacts.interface';
import { PopupFormComponent } from 'app/modules/clients/popup-form/popup-form.component';
import { ECustomFieldObject } from '../../../../v2/modules';

@Component({
  selector: 'cnc-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss'],
})
export class ClientFormComponent implements OnInit, OnDestroy {
  @Input() client :any;

  @ViewChild('tabGroup') tabGroup :MatTabGroup;

  title :string = 'New Client Profile';
  formData :UntypedFormGroup;
  submitted :boolean = false;
  loading :boolean = false;
  hide :boolean;
  same_as_business_address :boolean = false;

  tags :any = [];
  selectedTags :any = [];

  activeTabIndex :number = 0;
  maxTabIndex :number = 0;

  isCreating :boolean = false;

  // client user
  clientUser$ :Observable<string>;
  clientUserLoading = false;
  clientUserInput$ = new Subject<string>();
  private onDestroy$ = new Subject();

  clientPrimaryContacts :BusinessAccountContact[] = [];
  contacts$ :Observable<BusinessAccountContactInterface[]>;
  contacts :BusinessAccountContactInterface[] = [];

  params :any = {
    per_page: 25,
    page: 1,
  };
  salesRepAssigneeSubscription :Subscription;
  salesRepAssignee$ :Observable<Staff[]>;
  salesRepAssignee :Staff[] = [];
  salesRepAssigneeInput$ = new Subject<string>();
  salesRepAssigneeLoading$ :Observable<boolean>;
  allowedRoles :string = [
    UserRoleConstants.SALES_MEMBER,
    UserRoleConstants.ADMIN,
  ].join(',');
  featureFlag = EFeatureFlag;
  eCustomFieldObject = ECustomFieldObject;

  constructor(
    private clientSvc :ClientService,
    private fb :UntypedFormBuilder,
    public businessTypeSvc :BusinessTypeService,
    public activeModal :NgbActiveModal,
    private translate :TranslateService,
    private toastr :ToastrService,
    public tagSvc :ProductTagService,
    public clientTypeSvc :ClientTypeService,
    private cd :ChangeDetectorRef,
    private modal :NgbModal,
    public clientContactUtilsService :ClientContactUtilsService,
    private clientUserService :ClientUsersService,
    private shippingAddressService :ClientShippingAddressService,
    private staffService :StaffService,
    private authService :AuthService,
    private businessAccountContactsService :BusinessAccountContactsService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() {
    this.isCreating = !this.client?.id;

    this.tagSvc.getTags({ parent_only: true }, null);
    this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });

    this.getDataFromService();
    this.onInputSalesRepUpdated().subscribe();

    this.formData = this.fb.group({
      business_type_id: [''],
      business_name: ['', Validators.required],
      business_address: ['', Validators.required],
      business_address_line_2: [''],
      business_city: ['', Validators.required],
      business_state: ['', Validators.required],
      business_zip: ['', Validators.required],
      business_country: ['', Validators.required],
      client_grade: [''],
      phone: ['', Validators.required],
      email: [''],
      notes: [''],
      firstname: [''],
      lastname: [''],
      shipping_address: [''],
      shipping_address_line_2: [''],
      shipping_city: [''],
      shipping_state: [''],
      shipping_zip: [''],
      shipping_country: [''],
      tags: [[]],
      primaryContacts: [[]],
      fein: [[]],
      salesRepAssignee: [null],
      customFieldValues: [''],
      tax_exempt: [true],
      tax_rate: [1, Validators.min(0)],
    });

    if (this.client) {
      this.title = 'Update Client Profile';
      this.formData.patchValue(this.client);
      // this.formData.patchValue(this.client.user);
      this.formData.patchValue({ tags: this.client.tags.map((e) => e.id) });

      this.clientPrimaryContacts = this.client.primary_contacts;
      this.getBusinessAccountContactByBusinessAccountId().subscribe();

      this.shippingAddressService.get(this.client.id, {});
      const salesRepAssignee :Staff[] = [];
      this.client?.go_team_member?.forEach((data :any) => {
        salesRepAssignee.push(data.user);
      });

      this.formData.patchValue({
        salesRepAssignee: salesRepAssignee,
      });
    } else {
      this.setDefaultSalesRepAssignee();
    }
    this.getContacts().subscribe();
  }

  ngAfterViewInit() {
    this.maxTabIndex = this.tabGroup._allTabs.toArray().length - 1;
    this.cd.detectChanges();
  }

  onBusinessCountryChange(country :string) :void {
    this.formData.controls.business_country.setValue(country);
  }

  onSubmit() {
    this.submitted = true;
    if (this.formData.invalid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }
    this.loading = true;
    const data = this.formData.value;
    delete data.primaryContacts;

    const btype = this.businessTypeSvc.getTypeFromId(data.business_type_id);
    data.business_type = btype ? btype.type : '';

    if (this.client?.id) {
      this.clientSvc.saveProfile(this.client.id, data).subscribe(
        (resp :any) => {
          this.client = resp.record;
          this.loading = false;

          this.#noteSuccess();
          this.#next();
        },
        (errorResp :any) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
            text: errorResp.error.message,
            type: 'error',
            confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
          });
        },
      );
    } else {
      this.clientSvc.createProfile(data).subscribe(
        (resp :any) => {
          this.client = resp.record;
          this.shippingAddressService.get(this.client.id, {});
          this.loading = false;

          this.#noteSuccess({
            message: this.translate.instant(`MESSAGES.CREATE_SUCCESS.TEXT`),
            title: this.translate.instant(`MESSAGES.CREATE_SUCCESS.TITLE`),
          });
          this.#next();
        },
        (errorResp :any) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
            text: errorResp.error.message,
            type: 'error',
            confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
          });
        },
      );
    }
  }

  close() {
    if (this.client) {
      this.activeModal.close(this.client);
    } else {
      this.activeModal.dismiss('');
    }
  }
  addNewContact() {
    const modalRef = this.modal.open(PopupFormComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.client = this.client;
    modalRef.componentInstance.primaryContact = true;

    modalRef.result.then(
      (userAccount :BusinessAccountContact) => {
        this.getBusinessAccountContactByBusinessAccountId().subscribe();
        this.getContacts().subscribe();
      },
      (_) => {},
    );
  }

  updatePrimaryContacts(contactList :BusinessAccountContact[]) :void {
    if (contactList.length > this.clientPrimaryContacts.length) {
      // New primary contact added
      this.trySetUserAsPrimaryContact(contactList);
    } else if (contactList.length < this.clientPrimaryContacts.length) {
      // Primary contact removed
      this.tryUnsetUserAsPrimaryContact(contactList);
    }
  }

  clientContactSearch(
    term :string,
    item :BusinessAccountContactInterface,
  ) :boolean {
    const termInput :string = term.toLocaleLowerCase();

    return (
      item?.userAccount?.firstName.toLocaleLowerCase().indexOf(termInput) > -1 ||
      item?.userAccount?.lastName.toLocaleLowerCase().indexOf(termInput) > -1 ||
      item?.userAccount?.email.toLocaleLowerCase().indexOf(termInput) > -1
    );
  }

  updatePrimaryContactsV2(
    businessAccountContact :BusinessAccountContactInterface,
  ) :void {
    this.setPrimaryContact(businessAccountContact.id, true).subscribe();
  }

  onRemove(event :any) :void {
    this.setPrimaryContact(event.value.id, false).subscribe();
  }

  private setPrimaryContact(id :number, status :boolean) :Observable<any> {
    return this.businessAccountContactsService
      .setPrimaryContact({ id, primary: status })
      .pipe(
        tap(
          (response) => {
            if (status) {
              this.toastr.success('Successfully set as primary contact');
            } else {
              this.toastr.success('Successfully unset as primary contact');
            }
          },
          (error) => {
            this.toastr.error('Error request. Try again later.');
          },
        ),
        takeUntil(this.onDestroy$),
      );
  }

  clearPrimaryContacts() :void {
    const businessAccountId :number = this.client.id;
    this.unsetPrimaryContactForBusiness(businessAccountId).subscribe();
  }

  handleSelectedTabChange(e :MatTabChangeEvent) {
    this.activeTabIndex = e.index;
  }

  #next() {
    if (this.activeTabIndex > this.maxTabIndex) {
      // #sanitycheck
      throw new Error(`ClientFormComponent: Current tab is after the "final" tab.`);
    }

    if (this.activeTabIndex < this.maxTabIndex) {
      this.activeTabIndex++; // move to next tab
    } else {
      this.activeModal.close();
    }
  }
  #noteSuccess(options ?:{ title ?:string; message ?:string }) {
    const { title, message } = Object.assign(
      {
        // DEFAULTS
        message: this.translate.instant(`MESSAGES.UPDATE_SUCCESS.TEXT`),
        title: this.translate.instant(`MESSAGES.UPDATE_SUCCESS.TITLE`),
      },
      options,
    ); // override with provided options, if any

    if (
      this.isCreating &&
      this.activeTabIndex === this.maxTabIndex &&
      this.client?.id // #sanitycheck
    ) {
      const toast = this.toastr.success(
        `${message} <b>${this.translate.instant(`MESSAGES.CLICK_TO_VIEW`)}</b>`,
        title,
        { enableHtml: true },
      );
      const _toastDestroy$ = new Subject<void>();

      // handle click
      toast.onTap.pipe(takeUntil(_toastDestroy$)).subscribe(() => {
        router.navigate(['/clients', this.client.id, 'profile']);
      });

      // clean up subscriptions
      toast.onHidden.pipe(takeUntil(_toastDestroy$)).subscribe(() => {
        _toastDestroy$.next();
        _toastDestroy$.complete();
      });
    } else {
      this.toastr.success(message, title);
    }
  }

  private unsetPrimaryContactForBusiness(
    businessAccountId :number,
  ) :Observable<any> {
    return this.businessAccountContactsService
      .unsetPrimaryContactForBusiness(businessAccountId)
      .pipe(
        tap(
          (response) => {
            this.toastr.success(response.success);
          },
          (error) => {
            this.toastr.error(error?.error);
          },
        ),
        takeUntil(this.onDestroy$),
      );
  }

  private getBusinessAccountContactByBusinessAccountId() :Observable<any> {
    return this.businessAccountContactsService
      .getByBusinessAccountId({
        businessAccountId: this.client.id,
        primary: '1',
      })
      .pipe(
        tap((response) => {
          this.formData.patchValue({
            primaryContacts: response.data,
          });
          this.cd.detectChanges();
        }),
        takeUntil(this.onDestroy$),
      );
  }

  private trySetUserAsPrimaryContact(contactList :any[]) :void {
    contactList.forEach((contact :BusinessAccountContact) => {
      const idx :number = this.clientPrimaryContacts.findIndex(
        (primaryContact :BusinessAccountContact) =>
          primaryContact.id === contact.id,
      );
      if (idx >= 0) {
        return;
      }
      contact.primary_contact = true;
      this.clientPrimaryContacts.push(contact);
      this.clientContactUtilsService
        .addToActiveClientPrimaryContacts(contact)
        .subscribe(() => {}, takeUntil(this.onDestroy$));
    });
  }

  private tryUnsetUserAsPrimaryContact(
    contactList :BusinessAccountContact[],
  ) :void {
    const primaryContactList :BusinessAccountContact[] = [
      ...this.clientPrimaryContacts,
    ];
    primaryContactList.forEach(
      (primaryContact :BusinessAccountContact, idx :number) => {
        const userIdx :number = contactList.findIndex(
          (contact :BusinessAccountContact) => contact.id === primaryContact.id,
        );
        if (userIdx < 0) {
          primaryContact.primary_contact = false;
          this.clientPrimaryContacts.splice(idx, 1);
          this.clientContactUtilsService
            .removeFromActiveClientPrimaryContacts(primaryContact)
            .subscribe(() => {}, takeUntil(this.onDestroy$));
        }
      },
    );
  }

  private loadClientUsers() :void {
    this.clientUser$ = concat(
      this.clientUserInput$.pipe(
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => (this.clientUserLoading = true)),
        switchMap((term) =>
          this.clientUserService
            .searchClientUser({
              keyword: term,
              businessAccountId: this.client.id,
            })
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.clientUserLoading = false)),
              map((users) => {
                return users.reduce((userAccounts :any, user :any) => {
                  if (
                    this.clientPrimaryContacts.findIndex(
                      (row :any) => row.id == user.id,
                    ) < 0
                  ) {
                    userAccounts.push(user);
                  }

                  return userAccounts;
                }, []);
              }),
            ),
        ),
        takeUntil(this.onDestroy$),
      ),
    );
  }

  customTagsSearchFn(term :any, item :any) {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  handleSelected(form :any, $event) :void {
    if ($event.target.checked === true) {
      this.formData.patchValue({
        shipping_address: form.business_address,
        shipping_address_line_2: form.business_address_line_2,
        shipping_state: form.business_state,
        shipping_city: form.business_city,
        shipping_zip: form.business_zip,
        shipping_country: form.business_country,
      });
    } else {
      this.formData.patchValue({
        shipping_address: '',
        shipping_address_line_2: '',
        shipping_state: '',
        shipping_city: '',
        shipping_zip: '',
        shipping_country: '',
      });
    }
  }

  onCustomFieldChange(customField :string) :void {
    this.formData.get('customFieldValues').patchValue(customField);
  }

  private getDataFromService() :void {
    this.staffService.searchAssignee('', this.allowedRoles);
    this.salesRepAssignee$ = this.staffService.crmAssignees$;
    this.salesRepAssigneeLoading$ = this.staffService.loading$;
    this.contacts$ = this.businessAccountContactsService.contacts$;
  }

  private getContacts() :Observable<BusinessAccountContactInterface[]> {
    return this.contacts$.pipe(
      tap((contacts) => {
        this.contacts = contacts;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  trackSalesRepAssigneeByFn(item :Staff) :number {
    return item.id;
  }

  private setDefaultSalesRepAssignee() :void {
    const loginUser :Staff = this.authService.loginUser;
    const userDetails = [];
    userDetails.push({
      id: loginUser.id,
      firstname: loginUser.firstname,
      lastname: loginUser.lastname,
      email: loginUser.email,
    });
    this.formData.patchValue({
      salesRepAssignee: userDetails,
    });
  }

  private onInputSalesRepUpdated() :Observable<string> {
    return this.salesRepAssigneeInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((name :string) => {
        this.staffService.searchAssignee(name, this.allowedRoles);
      }),
      takeUntil(this.onDestroy$),
    );
  }
}
